import { useState, useEffect, useMemo } from 'react';
import { sendChatMessage, fetchModels } from '../API';

/**
 * useChat — the production chat controller, extracted verbatim from ChatDrawer
 * so a sibling demo controller (useDemoChat) can implement the same interface and
 * swap into the shared <ChatDrawerView/> with zero changes to prod behavior.
 *
 * Returns a controller object consumed by ChatDrawerView. The view owns refs and
 * the isOpen-gated scroll/focus effects; everything else lives here.
 */

// Module-level cache — persists across drawer open/close, clears on page reload.
let cachedModels = null;
let cachedMessages = [];
let cachedChatId = null;
let cachedPatientId = undefined;

export default function useChat({ patientId = null, refreshUserFiles } = {}) {
  const isPatientMode = !patientId;

  const [models, setModels] = useState(cachedModels);
  const [messages, _setMessages] = useState(() =>
    cachedPatientId === patientId ? cachedMessages : []
  );
  const [chatId, _setChatId] = useState(() =>
    cachedPatientId === patientId ? cachedChatId : null
  );
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedModelIndex, setSelectedModelIndex] = useState(0);

  const setMessages = (update) => {
    _setMessages(prev => {
      const next = typeof update === 'function' ? update(prev) : update;
      cachedMessages = next;
      cachedPatientId = patientId;
      return next;
    });
  };

  const setChatId = (id) => {
    _setChatId(id);
    cachedChatId = id;
    cachedPatientId = patientId;
  };

  const modelList = models?.models || [];
  const defaultModel = useMemo(
    () => modelList.find(m => m.isDefault) || modelList[0],
    [models]
  );

  // Fetch available models once.
  useEffect(() => {
    if (cachedModels) return;
    let cancelled = false;
    fetchModels().then(data => {
      if (cancelled) return;
      cachedModels = data;
      setModels(data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Reset state when patient changes (doctor switching patients).
  useEffect(() => {
    if (cachedPatientId !== undefined && cachedPatientId !== patientId) {
      setMessages([]);
      setChatId(null);
      setInputValue('');
      setLoading(false);
      setError(null);
    }
    cachedPatientId = patientId;
  }, [patientId]);

  async function send() {
    const text = inputValue.trim();
    if (!text || loading) return;

    const timestamp = new Date().toISOString();
    const selected = modelList[selectedModelIndex] || defaultModel;
    const provider = selected?.provider || models?.defaultProvider || 'bedrock';
    const model = selected?.model || null;

    // Optimistic: show user message immediately.
    setMessages(prev => [...prev, { role: isPatientMode ? 'user' : 'doctor', content: text, timestamp }]);
    setInputValue('');
    setLoading(true);
    setError(null);

    try {
      const result = await sendChatMessage(patientId, text, chatId, provider, model);
      setChatId(result.chatId);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: result.reply,
        timestamp: result.timestamp,
      }]);
      if (result.protocolEdited && refreshUserFiles) {
        refreshUserFiles(true);
      }
    } catch (err) {
      console.error('Chat error:', err);
      const is403 = err.message?.includes('Premium');
      setError(is403
        ? 'Elis chat requires a Premium subscription.'
        : 'Failed to get response. Please try again.'
      );
      setMessages(prev => prev.slice(0, -1));
      setInputValue(text);
    } finally {
      setLoading(false);
    }
  }

  return {
    // state
    messages,
    loading,
    error,
    inputValue,
    setInputValue,
    isPatientMode,
    // model picker — hidden when there's no choice (prod exposes a single model
    // and must not reveal which one it is)
    showModelSelect: modelList.length > 1,
    modelList,
    selectedModelIndex,
    setSelectedModelIndex,
    modelWarning: modelList[selectedModelIndex]?.warning || '',
    // suggestion chips (prod: populate the input, do NOT auto-send)
    suggestions: [
      { id: 'trend', label: 'How are my results trending?', icon: 'trend' },
      { id: 'alert', label: 'What markers need attention?', icon: 'alert' },
      { id: 'lifestyle', label: 'Suggest lifestyle changes', icon: 'lifestyle' },
    ],
    suggestionsAlwaysVisible: false,
    onSuggestion: (s) => setInputValue(s.label),
    // input
    inputLocked: false,
    placeholder: isPatientMode ? 'Ask Elis a question...' : 'Ask about this patient...',
    showSendButton: true,
    canSend: !loading && !!inputValue.trim() && modelList.length > 0,
    emptyStateText: null, // view falls back to the patient/doctor-aware default copy
    // actions
    send,
  };
}
